export type User = {
  id: string;
  displayName: string;
  pictureUrl: string | null;
};
export type NoteSummary = {
  id: string;
  title: string;
  excerpt: string;
  revision: number;
  updatedAt: number;
  role: "owner" | "editor" | "viewer";
};
export type NoteDetail = {
  id: string;
  title: string;
  body: string;
  revision: number;
  updatedAt: number;
  role: "owner" | "editor" | "viewer";
  mediaToken: string;
  lease: null | {
    mine: boolean;
    displayName: string | null;
    expiresAt: number | null;
  };
};

let token: string | null = null;
try {
  token = sessionStorage.getItem("matomemo:token");
} catch {
  /* In-memory authentication works when storage is unavailable. */
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
  }
}

export function setToken(value: string): void {
  token = value;
  try {
    sessionStorage.setItem("matomemo:token", value);
  } catch {
    /* Keep the in-memory token. */
  }
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (init.body && !(init.body instanceof FormData))
    headers.set("Content-Type", "application/json");
  const response = await fetch(path, { ...init, headers });
  const responseText = await response.text();
  let value: unknown;
  try {
    value = JSON.parse(responseText);
  } catch {
    value = { message: responseText.slice(0, 300) || "通信に失敗しました" };
  }
  if (!response.ok) {
    const record =
      typeof value === "object" && value !== null
        ? (value as Record<string, unknown>)
        : {};
    const message =
      typeof record.message === "string"
        ? record.message
        : typeof record.error === "string"
          ? record.error
          : "通信に失敗しました";
    throw new ApiError(message, response.status, record);
  }
  return value as T;
}

export async function authenticate(lineAccessToken: string): Promise<User> {
  const result = await api<{ token: string; user: User }>("/api/auth/line", {
    method: "POST",
    body: JSON.stringify({ accessToken: lineAccessToken }),
  });
  setToken(result.token);
  return result.user;
}

export function authHeaders(value?: string): HeadersInit {
  return value
    ? { Authorization: `Bearer ${value}` }
    : token
      ? { Authorization: `Bearer ${token}` }
      : {};
}
